/**
 * Chat input component with image attachment support
 * Handles text input, image attachments, and permissions
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { css } from '@emotion/react';
import { toast } from 'react-toastify';
import { colors, spacing, fontStyles } from '../../../styles';
import { backendUrl } from '../../../constants';
import Button from '../../Button';
import { checkAndRequestPhotoPermissions } from '../../../utils/cameraPermissions';
import { blobToBase64, compressImage, isValidImageFormat } from '../../../utils/imageProcessing';
import SendIcon from '../Send.svg';
import AttachmentButton from './AttachmentButton';
import ImagePreviewRow from './ImagePreviewRow';
import { AttachedImage } from './ImageThumbnail';

interface ChatInputProps {
  onSendMessage: (text: string, images: AttachedImage[]) => void;
  isLoading: boolean;
  disabled?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, isLoading, disabled = false }) => {
  const [inputValue, setInputValue] = useState('');
  const [attachedImages, setAttachedImages] = useState<AttachedImage[]>([]);
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAttachClick = async () => {
    try {
      setIsRequestingPermission(true);

      // On web, use standard file input
      if (!Capacitor.isNativePlatform()) {
        fileInputRef.current?.click();
        return;
      }

      // On mobile, check and request permissions
      const permissionResult = await checkAndRequestPhotoPermissions();

      if (!permissionResult.granted) {
        toast.error(permissionResult.message || 'Photo access denied');
        return;
      }

      // Open photo picker
      await openPhotoPicker();
    } catch (error) {
      console.error('Error handling attachment:', error);
      toast.error('Failed to open photo picker');
    } finally {
      setIsRequestingPermission(false);
    }
  };

  const openPhotoPicker = async () => {
    if (isRequestingPermission) {
      return; // Prevent multiple simultaneous picker requests
    }

    setIsRequestingPermission(true);
    try {
      // Use pickImages for multiple selection
      const result = await Camera.pickImages({
        quality: 90,
        limit: 4, // Max 4 images
      });

      if (!result.photos || result.photos.length === 0) {
        return; // User cancelled
      }

      // Process each photo
      const newImages: AttachedImage[] = [];

      for (const [index, photo] of result.photos.entries()) {
        // Read image as blob then convert to base64
        const response = await fetch(photo.webPath);
        const blob = await response.blob();

        // Validate format first (before size check)
        if (!isValidImageFormat(blob.type)) {
          toast.error(`Photo ${index + 1} has unsupported format (${blob.type}). Please use PNG, JPEG, GIF, or WebP.`);
          continue;
        }

        // Validate size (5MB limit)
        if (blob.size > 5 * 1024 * 1024) {
          toast.error(`Photo ${index + 1} is too large (max 5MB)`);
          continue;
        }

        // Convert to base64
        let base64 = await blobToBase64(blob);

        // Compress image to reduce payload size (max width 1024px for vision models)
        // Backend will further compress based on detail level (high=500KB, low=150KB)
        base64 = await compressImage(base64, 1024);

        newImages.push({
          id: `img-${Date.now()}-${Math.random()}`,
          dataUrl: base64,
          fileName: `image.${photo.format || 'jpg'}`,
          mimeType: `image/${photo.format || 'jpeg'}`,
          sizeBytes: blob.size,
        });
      }

      // Add to state
      setAttachedImages((prev) => {
        const combined = [...prev, ...newImages];
        // Limit to 4 total images
        if (combined.length > 4) {
          toast.warning('Maximum 4 images allowed');
          return combined.slice(0, 4);
        }
        return combined;
      });
    } catch (error) {
      console.error('Error picking images:', error);
      toast.error('Failed to select images');
    } finally {
      setIsRequestingPermission(false);
    }
  };

  const handleRemoveImage = (id: string) => {
    setAttachedImages((prev) => prev.filter((img) => img.id !== id));
  };

  // Handle web file input
  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      const newImages: AttachedImage[] = [];

      // Process up to 4 files
      const filesToProcess = Array.from(files).slice(0, 4);

      for (const file of filesToProcess) {
        // Validate format first (PNG, JPEG, GIF, WebP only)
        if (!isValidImageFormat(file.type)) {
          toast.error(`${file.name} has unsupported format (${file.type}). Please use PNG, JPEG, GIF, or WebP.`);
          continue;
        }

        // Validate size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 5MB)`);
          continue;
        }

        // Convert to base64
        let base64 = await blobToBase64(file);

        // Compress image to reduce payload size (max width 1024px for vision models)
        // Backend will further compress based on detail level (high=500KB, low=150KB)
        base64 = await compressImage(base64, 1024);

        newImages.push({
          id: `img-${Date.now()}-${Math.random()}`,
          dataUrl: base64,
          fileName: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
        });
      }

      // Add to state
      setAttachedImages((prev) => {
        const combined = [...prev, ...newImages];
        // Limit to 4 total images
        if (combined.length > 4) {
          toast.warning('Maximum 4 images allowed');
          return combined.slice(0, 4);
        }
        return combined;
      });
    } catch (error) {
      console.error('Error processing files:', error);
      toast.error('Failed to process images');
    } finally {
      // Always reset file input (even on error) so user can retry
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setIsRequestingPermission(false);
    }
  };

  const handleSend = () => {
    if (disabled || isLoading) {
      return;
    }

    if (!inputValue.trim() && attachedImages.length === 0) {
      return;
    }

    onSendMessage(inputValue.trim(), attachedImages);

    // Clear state
    setInputValue('');
    setAttachedImages([]);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !Capacitor.isNativePlatform()) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  // Sprint 3a: Preconnect on input focus — warm TCP/TLS connection
  const preconnectFired = useRef(false);
  const handleFocus = useCallback(() => {
    if (preconnectFired.current) return;
    preconnectFired.current = true;
    fetch(`${backendUrl}/health-check/`, { method: 'HEAD', keepalive: true }).catch(() => undefined);
  }, []);

  // Auto-resize textarea (matching original ChatPage behavior)
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 120;

      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
      textareaRef.current.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
    }
  }, [inputValue]);

  const canSend = !disabled && !isLoading && (inputValue.trim() || attachedImages.length > 0);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.deci};
      `}
    >
      {/* Hidden file input for web */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileInputChange}
        css={css`
          display: none;
        `}
      />

      {/* Image Preview Row - Only show if images attached */}
      {attachedImages.length > 0 && <ImagePreviewRow images={attachedImages} onRemove={handleRemoveImage} />}

      {/* Input Row - Matches original ChatPage styling exactly */}
      <div
        css={css`
          position: relative;
          display: flex;
          contain: layout;
          align-items: center;
          gap: ${spacing.centi};
        `}
      >
        {/* Attach Button (+) */}
        <AttachmentButton onClick={handleAttachClick} disabled={disabled || isLoading || isRequestingPermission} />

        {/* Text Input - Original styling from ChatPage */}
        <textarea
          ref={textareaRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder="Write a message..."
          rows={1}
          css={css`
            width: 100%;
            padding: 20px;
            padding-right: 60px;
            border: 0.5px solid rgba(0, 0, 0, 0.2);
            border-radius: 16px;
            background-color: ${colors.snow};
            color: var(--text-color);
            ${fontStyles.mouse}
            resize: none;
            overflow-y: hidden;
            min-height: 56px;
            max-height: 120px;
            line-height: 1.4;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            color-scheme: light;

            &::placeholder {
              color: rgba(0, 0, 0, 0.4);
            }

            &:focus {
              outline: none;
              border-color: ${colors.petrolBlue};
              box-shadow: 0 0 0 3px rgba(23, 110, 193, 0.08);
            }

            html[data-theme='dark'] & {
              border-color: rgba(255, 255, 255, 0.2);
              background-color: transparent;
              color-scheme: dark;

              &::placeholder {
                color: rgba(255, 255, 255, 0.4);
              }

              &:focus {
                border-color: ${colors.skyBlue};
                box-shadow: 0 0 0 3px rgba(40, 181, 189, 0.08);
              }
            }
          `}
        />

        {/* Send Button - Original styling from ChatPage */}
        <Button
          onClick={handleSend}
          disabled={!canSend}
          css={css`
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            width: 40px;
            height: 40px;
            padding: 0;
            min-width: unset;
            background-color: ${colors.petrolBlue};
            color: ${colors.sugar};
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            transition: all 0.2s ease;

            &:disabled {
              background-color: ${colors.cloud};
              color: ${colors.silver};
              box-shadow: none;
            }

            html[data-theme='dark'] & {
              background-color: ${colors.skyBlue};
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
              &:disabled {
                background-color: ${colors.tungsten};
                color: ${colors.silver};
              }
            }

            &:hover:not(:disabled) {
              background-color: ${colors.petrolBlue};
              opacity: 0.9;
            }

            html[data-theme='dark'] &:hover:not(:disabled) {
              background-color: ${colors.skyBlue};
              opacity: 0.9;
            }

            & img {
              width: 18px;
              height: 18px;
            }
          `}
        >
          <img src={SendIcon} />
        </Button>
      </div>
    </div>
  );
};

export default ChatInput;
